/**
 * Availability Redux Slice
 * Manages astrologer availability state and actions
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as availabilityApi from '../../services/availabilityApi';

// Async thunk: Save availability
export const saveAvailability = createAsyncThunk(
  'availability/save',
  async ({ date, slots }, { rejectWithValue }) => {
    try {
      const response = await availabilityApi.setAvailability(date, slots);
      return { response, date };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk: Fetch available dates
export const fetchAvailableDates = createAsyncThunk(
  'availability/fetchDates',
  async ({ astrologerId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await availabilityApi.getAvailableDates(
        astrologerId,
        startDate,
        endDate
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk: Fetch availability for specific date
export const fetchAvailability = createAsyncThunk(
  'availability/fetchSlots',
  async ({ astrologerId, date }, { rejectWithValue }) => {
    try {
      const response = await availabilityApi.getAvailability(astrologerId, date);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk: Fetch astrologers list
export const fetchAstrologers = createAsyncThunk(
  'availability/fetchAstrologers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await availabilityApi.getAstrologers();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const availabilitySlice = createSlice({
  name: 'availability',
  initialState: {
    selectedDate: null,
    selectedSlots: [],
    availableDates: [],
    availableSlots: [],
    astrologers: [],
    selectedAstrologer: null,
    existingAvailability: null,
    loading: false,
    error: null,
    saveStatus: 'idle', // 'idle' | 'loading' | 'success' | 'error'
  },
  reducers: {
    setSelectedDate: (state, action) => {
      console.log('[Redux] Selected date:', action.payload);
      state.selectedDate = action.payload;
      state.selectedSlots = []; // Reset slots when date changes
    },
    setSelectedAstrologer: (state, action) => {
      console.log('[Redux] Selected astrologer:', action.payload);
      state.selectedAstrologer = action.payload;
      state.selectedDate = null;
      state.availableDates = [];
      state.availableSlots = [];
    },
    toggleSlot: (state, action) => {
      const { period, index } = action.payload;
      const slotGroup = state.selectedSlots.find(s => s.period === period);

      if (slotGroup) {
        const indexPos = slotGroup.slot_indices.indexOf(index);
        if (indexPos > -1) {
          // Remove slot
          slotGroup.slot_indices.splice(indexPos, 1);
          console.log(`[Redux] Slot ${index} (${period}) deselected`);
          // Remove group if empty
          if (slotGroup.slot_indices.length === 0) {
            state.selectedSlots = state.selectedSlots.filter(s => s.period !== period);
          }
        } else {
          // Add slot
          slotGroup.slot_indices.push(index);
          slotGroup.slot_indices.sort((a, b) => a - b); // Keep sorted
          console.log(`[Redux] Slot ${index} (${period}) selected`);
        }
      } else {
        // Create new group
        state.selectedSlots.push({
          period,
          slot_indices: [index]
        });
        console.log(`[Redux] Slot ${index} (${period}) selected (new group)`);
      }
    },
    selectAllPeriod: (state, action) => {
      const { period, indices } = action.payload;
      const slotGroup = state.selectedSlots.find(s => s.period === period);

      if (slotGroup) {
        slotGroup.slot_indices = [...indices].sort((a, b) => a - b);
      } else {
        state.selectedSlots.push({
          period,
          slot_indices: [...indices].sort((a, b) => a - b)
        });
      }
      console.log(`[Redux] Selected all ${period} slots:`, indices.length);
    },
    clearSelection: (state) => {
      console.log('[Redux] Cleared all slot selections');
      state.selectedSlots = [];
    },
    clearPeriodSelection: (state, action) => {
      const period = action.payload;
      state.selectedSlots = state.selectedSlots.filter(s => s.period !== period);
      console.log(`[Redux] Cleared ${period} slot selections`);
    },
    resetSaveStatus: (state) => {
      state.saveStatus = 'idle';
      state.error = null;
    },
    resetAvailabilityState: (state) => {
      state.selectedDate = null;
      state.selectedSlots = [];
      state.availableSlots = [];
      state.saveStatus = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Save availability
      .addCase(saveAvailability.pending, (state) => {
        state.saveStatus = 'loading';
        state.error = null;
      })
      .addCase(saveAvailability.fulfilled, (state, action) => {
        state.saveStatus = 'success';
        state.selectedSlots = [];
        console.log('[Redux] Availability saved successfully');
      })
      .addCase(saveAvailability.rejected, (state, action) => {
        state.saveStatus = 'error';
        state.error = action.payload;
        console.error('[Redux] Save availability failed:', action.payload);
      })

      // Fetch available dates
      .addCase(fetchAvailableDates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableDates.fulfilled, (state, action) => {
        state.loading = false;
        state.availableDates = action.payload.available_dates || [];
        console.log(`[Redux] Loaded ${action.payload.total_available_dates || 0} available dates`);
      })
      .addCase(fetchAvailableDates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[Redux] Fetch available dates failed:', action.payload);
      })

      // Fetch availability slots
      .addCase(fetchAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.availableSlots = action.payload.available_slots || [];
        console.log(`[Redux] Loaded ${action.payload.total_available || 0} available slots`);
      })
      .addCase(fetchAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[Redux] Fetch availability failed:', action.payload);
      })

      // Fetch astrologers
      .addCase(fetchAstrologers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAstrologers.fulfilled, (state, action) => {
        state.loading = false;
        state.astrologers = action.payload || [];
        console.log(`[Redux] Loaded ${action.payload?.length || 0} astrologers`);
      })
      .addCase(fetchAstrologers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('[Redux] Fetch astrologers failed:', action.payload);
      });
  },
});

export const {
  setSelectedDate,
  setSelectedAstrologer,
  toggleSlot,
  selectAllPeriod,
  clearSelection,
  clearPeriodSelection,
  resetSaveStatus,
  resetAvailabilityState,
} = availabilitySlice.actions;

export default availabilitySlice.reducer;
